/**
 * Does the user ask for reduced motion?
 *
 * `styles.scss` carries a universal `@media (prefers-reduced-motion: reduce)` reset that flattens
 * every CSS animation and transition in the app, Angular Material's and Bootstrap's included. It
 * cannot reach a scroll asked for in JAVASCRIPT: `scrollIntoView({behavior: 'smooth'})` and
 * `window.scroll({behavior: 'smooth'})` take their behaviour from the call rather than from the
 * cascade, so the CSS `scroll-behavior` declaration is not consulted at all. This is what those
 * call sites ask instead, and `npm run audit:motion` refuses a new one that does not.
 *
 * GUARDED, and for the same reason every storage read in this app is: `matchMedia` is a property on
 * `window` that a test double, a server-side render or a sandboxed document need not provide, and a
 * throw here would land inside a click handler. An environment that cannot answer is treated as NOT
 * asking for reduced motion — the app's ordinary behaviour, rather than silently degrading everyone
 * to instant scrolling on the strength of a missing API.
 */
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

export function prefersReducedMotion(view: Window = window): boolean {
  try {
    return view.matchMedia?.(REDUCED_MOTION_QUERY).matches === true;
  } catch {
    return false;
  }
}

/**
 * The `behavior` to hand a scroll call: the user's answer, in the one word those APIs take.
 * A helper rather than a ternary at each site, so the two cannot drift apart and a third site has
 * something to copy that is already right.
 */
export function scrollBehavior(view: Window = window): ScrollBehavior {
  return prefersReducedMotion(view) ? 'auto' : 'smooth';
}
